import { CustomerPartOrderPostLineItem, LineItemType } from "@ncs/ncs-api"

import { Shipment, ShopContextState } from "~/contexts"
import { VoucherifyDiscount } from "./CheckoutEcomm"

export const makeNewOrderLineItems = (
	cart: ShopContextState["cart"],
	shipments: Shipment[] | null,
	accessorials: ShopContextState["checkout"]["accessorials"],
	freightTotal: number,
	tariffEstimate?: { amount: number; tax: number } | null,
	voucherifyDiscounts?: VoucherifyDiscount | null,
	omitFreightAndTaxes = false
): CustomerPartOrderPostLineItem[] => {
	const partLineItems = (shipments ?? []).reduce((items, shipment) => {
		return [
			...items,
			...shipment.parts.flatMap((shippitPart) => {
				const cartPart = cart.find(({ part }) => {
					return Number(part.id) === Number(shippitPart.id)
				})

				// The order line item requires data from both the shippit response as well as from the
				// cart. It will normally be there, but if you've just removed it from the cart there
				// may be a few frames where shippit has a part but it's not in the cart, so weed
				// that out here.
				if (!cartPart) {
					return []
				}

				if (!shipment.service) {
					throw new Error("Shipment does not have a service selected")
				}

				const newLineItem: CustomerPartOrderPostLineItem = {
					partId: cartPart.part.part.id,
					onlinePartId: cartPart.part.id,
					taxRateUsed: ((shippitPart.taxRate ?? 0) / 100).toFixed(7),
					unitTax: (
						shippitPart.price *
						shippitPart.quantity *
						((shippitPart.taxRate ?? 0) / 100)
					).toFixed(7),
					unitPrice: shippitPart.price,
					description: shippitPart.title ?? "",
					quantity: shippitPart.quantity,
					shipping: shipment.service,
					lineTypeId: LineItemType.Parts,
				}

				return [newLineItem]
			}),
		]
	}, [] as CustomerPartOrderPostLineItem[])

	const accessorialLineItems = accessorials.map(
		(accessorial): CustomerPartOrderPostLineItem => ({
			partId: null,
			onlinePartId: null,
			taxRateUsed: "0",
			unitTax: "0",
			shipping: null,
			quantity: 1,
			unitPrice: accessorial.rate,
			description: accessorial.lineItemDescription,
			lineTypeId: accessorial.lineItemTypeId.toString() as LineItemType,
		})
	)

	const freightLineItem =
		omitFreightAndTaxes ?
			[]
		:	[
				{
					partId: null,
					onlinePartId: null,
					taxRateUsed: "0",
					unitTax: "0",
					shipping: null,
					quantity: 1,
					unitPrice: freightTotal,
					description: "Freight",
					lineTypeId: LineItemType.Freight,
				},
			]

	if (tariffEstimate?.amount) {
		partLineItems.push({
			description: "Tariff Surcharge",
			lineTypeId: LineItemType.TariffSurcharge,
			onlinePartId: null,
			partId: null,
			quantity: 1,
			shipping: null,
			taxRateUsed: (tariffEstimate.tax / tariffEstimate.amount).toFixed(2),
			unitPrice: tariffEstimate.amount,
			unitTax: (tariffEstimate.tax ?? 0).toFixed(2),
		})
	}

	if (voucherifyDiscounts) {
		partLineItems.push({
			description: "Promo Code Discount",
			lineTypeId: LineItemType.PromoCode,
			onlinePartId: null,
			partId: null,
			quantity: 1,
			shipping: null,
			taxRateUsed: "0",
			unitPrice: -(voucherifyDiscounts.totalDiscountAmount / 100),
			unitTax: "0",
		})
	}

	return [...partLineItems, ...accessorialLineItems, ...freightLineItem]
}
